import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Pencil, X, Archive, ArchiveRestore, Trash2, ChevronDown, ChevronRight, ListPlus } from "lucide-react";
import { extractApiError } from "@/components/PanelState";

// ============================================================================
// TechProfileCustomFieldsPanel — tenant admin manages the schema for
// tech_profiles.custom_fields. Fields defined here appear as inputs inside
// TechProfileFormModal. Reporting-friendly: every tech gets the same set of
// fields, values validated against defs on the server.
// (2026-08-10, Juliana / CSR feedback.)
// ============================================================================

const FIELD_TYPE_LABEL = {
  text: "Text",
  number: "Number",
  boolean: "Yes / No",
  select: "Single choice",
  multiselect: "Multiple choice",
};

const INPUT_CLS = "w-full border border-[#e7e9ee] rounded-md px-3 py-2 text-[13px] bg-white outline-none focus:border-[#0072bc]";

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64) || "field";
}

export default function TechProfileCustomFieldsPanel({ tenantSlug }) {
  const [open, setOpen] = useState(false);
  const [defs, setDefs] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, key, label, field_type, options[], required}
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoadErr(null);
    try {
      const { data } = await api.get("/tech-profile-fields", {
        params: { tenant_slug: tenantSlug, include_archived: true },
      });
      setDefs(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadErr(extractApiError(err)?.message || "Failed to load custom field defs");
    }
  };

  useEffect(() => { if (open) load(); }, [open, tenantSlug]);

  const archiveToggle = async (def) => {
    setBusyId(def.id);
    try {
      await api.patch(`/tech-profile-fields/${def.id}`, { archived: !def.archived }, { params: { tenant_slug: tenantSlug } });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const hardDelete = async (def) => {
    if (!window.confirm(`Delete "${def.label}" and strip all stored values from every tech? This cannot be undone. Prefer Archive for the usual case.`)) return;
    setBusyId(def.id);
    try {
      await api.delete(`/tech-profile-fields/${def.id}`, { params: { tenant_slug: tenantSlug } });
      await load();
    } catch (err) {
      alert(extractApiError(err)?.message || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="border border-[#e7e9ee] rounded-lg bg-white" data-testid="tech-profile-custom-fields-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="tp-custom-fields-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#f9fafb]"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} strokeWidth={1.8} /> : <ChevronRight size={16} strokeWidth={1.8} />}
          <ListPlus size={16} strokeWidth={1.6} className="text-[#0072bc]" />
          <span className="text-[13.5px] font-semibold text-[#0e1420]">Custom fields</span>
          <span className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">
            {defs.filter((d) => !d.archived).length} active
            {defs.filter((d) => d.archived).length > 0 && (
              <span className="ml-1.5 text-[color:var(--cc-ink-muted)]">
                · {defs.filter((d) => d.archived).length} archived
              </span>
            )}
          </span>
        </div>
        <span className="text-[11.5px] text-[color:var(--cc-ink-muted)]">
          Tenant-defined attributes attached to every tech profile
        </span>
      </button>

      {open && (
        <div className="border-t border-[#e7e9ee] p-4 space-y-3">
          {loadErr && (
            <div className="bg-[#fff5f5] border border-[#f5c2c2] rounded-lg p-3 text-[12.5px] text-[#7a1c1c]">
              {loadErr}
            </div>
          )}
          {defs.length === 0 && !loadErr && (
            <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] italic py-2">
              No custom fields yet. Add one to capture structured attributes like <b>height bracket</b>, <b>tool kit</b>, or <b>certifications</b> across every tech.
            </div>
          )}
          {defs.length > 0 && (
            <div className="border border-[#e7e9ee] rounded-lg overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Label</th>
                    <th className="text-left px-3 py-2 font-semibold">Key</th>
                    <th className="text-left px-3 py-2 font-semibold">Type</th>
                    <th className="text-left px-3 py-2 font-semibold">Options</th>
                    <th className="text-right px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...defs].sort((a, b) => (a.sort_order - b.sort_order) || a.label.localeCompare(b.label)).map((d) => (
                    <tr
                      key={d.id}
                      data-testid={`tp-custom-field-row-${d.key}`}
                      className={`border-t border-[#e7e9ee] ${d.archived ? "opacity-60" : ""}`}
                    >
                      <td className="px-3 py-2 font-semibold text-[#0e1420]">
                        {d.label}
                        {d.required && <span className="ml-1.5 text-[10px] font-mono-data uppercase tracking-widest text-[#b45309]">req</span>}
                        {d.archived && <span className="ml-1.5 text-[10px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)]">archived</span>}
                      </td>
                      <td className="px-3 py-2 font-mono-data text-[12px] text-[color:var(--cc-ink-muted)]">{d.key}</td>
                      <td className="px-3 py-2 text-[12px]">{FIELD_TYPE_LABEL[d.field_type] || d.field_type}</td>
                      <td className="px-3 py-2 text-[12px] text-[color:var(--cc-ink-muted)]">
                        {(d.options && d.options.length > 0) ? d.options.join(", ") : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            type="button"
                            onClick={() => setEditing({ ...d })}
                            data-testid={`tp-custom-field-edit-${d.key}`}
                            className="p-1.5 hover:bg-[#f5f6f8] rounded-md"
                            title="Edit"
                          >
                            <Pencil size={14} strokeWidth={1.7} />
                          </button>
                          <button
                            type="button"
                            disabled={busyId === d.id}
                            onClick={() => archiveToggle(d)}
                            data-testid={`tp-custom-field-archive-${d.key}`}
                            className="p-1.5 hover:bg-[#f5f6f8] rounded-md disabled:opacity-50"
                            title={d.archived ? "Restore" : "Archive"}
                          >
                            {d.archived ? <ArchiveRestore size={14} strokeWidth={1.7} /> : <Archive size={14} strokeWidth={1.7} />}
                          </button>
                          <button
                            type="button"
                            disabled={busyId === d.id}
                            onClick={() => hardDelete(d)}
                            data-testid={`tp-custom-field-delete-${d.key}`}
                            className="p-1.5 hover:bg-[#fdecec] text-[#b91c1c] rounded-md disabled:opacity-50"
                            title="Delete (strips values from all profiles)"
                          >
                            <Trash2 size={14} strokeWidth={1.7} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div>
            <button
              type="button"
              onClick={() => setEditing({ key: "", label: "", field_type: "text", options: [], required: false, sort_order: 100 })}
              data-testid="tp-custom-field-new-btn"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 bg-[#0072bc] text-white rounded-lg hover:bg-[#005a95]"
            >
              <Plus size={14} strokeWidth={2} /> New custom field
            </button>
          </div>
        </div>
      )}

      {editing && (
        <FieldDefFormModal
          tenantSlug={tenantSlug}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}


function FieldDefFormModal({ tenantSlug, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    key: initial?.key || "",
    label: initial?.label || "",
    field_type: initial?.field_type || "text",
    options: (initial?.options || []).join("\n"),
    required: !!initial?.required,
    sort_order: initial?.sort_order ?? 100,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-slug the key from the label on create (edit locks key).
  useEffect(() => {
    if (!isEdit) setF("key", slugify(form.label));
  }, [form.label]);

  const needsOptions = form.field_type === "select" || form.field_type === "multiselect";
  const parseOptions = () => form.options.split("\n").map((s) => s.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        const patch = {
          label: form.label.trim(),
          required: !!form.required,
          sort_order: Number(form.sort_order) || 100,
        };
        if (needsOptions) patch.options = parseOptions();
        await api.patch(`/tech-profile-fields/${initial.id}`, patch, { params: { tenant_slug: tenantSlug } });
      } else {
        const payload = {
          key: form.key.trim(),
          label: form.label.trim(),
          field_type: form.field_type,
          options: needsOptions ? parseOptions() : [],
          required: !!form.required,
          sort_order: Number(form.sort_order) || 100,
        };
        await api.post("/tech-profile-fields", payload, { params: { tenant_slug: tenantSlug } });
      }
      await onSaved();
    } catch (err) {
      setError(extractApiError(err)?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="tp-custom-field-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-[520px] shadow-xl border border-[#e7e9ee] my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e7e9ee]">
          <div>
            <h2 className="font-display font-bold text-[17px] text-[#0e1420]">
              {isEdit ? "Edit custom field" : "New custom field"}
            </h2>
            <p className="text-[12px] text-[color:var(--cc-ink-muted)] mt-0.5">
              {isEdit ? "Label + options can be renamed. Type + key are locked after create." : "Applies to every tech profile in this tenant."}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#f5f6f8] rounded-md">
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Label</label>
            <input
              data-testid="tp-custom-field-label"
              className={INPUT_CLS + " mt-1"}
              value={form.label}
              onChange={(e) => setF("label", e.target.value)}
              maxLength={120}
              required
              placeholder="e.g. Height Bracket"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Key (slug)</label>
            <input
              data-testid="tp-custom-field-key"
              className={INPUT_CLS + " mt-1 font-mono-data"}
              value={form.key}
              onChange={(e) => setF("key", slugify(e.target.value))}
              maxLength={64}
              required
              disabled={isEdit}
              placeholder="e.g. height_bracket"
            />
            <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">
              Used in future filtering / reporting queries. Locked after create.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Type</label>
              <select
                data-testid="tp-custom-field-type"
                className={INPUT_CLS + " mt-1"}
                value={form.field_type}
                onChange={(e) => setF("field_type", e.target.value)}
                disabled={isEdit}
              >
                {Object.entries(FIELD_TYPE_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Sort order</label>
              <input
                data-testid="tp-custom-field-sort"
                type="number"
                className={INPUT_CLS + " mt-1"}
                value={form.sort_order}
                onChange={(e) => setF("sort_order", e.target.value)}
              />
            </div>
          </div>
          {needsOptions && (
            <div>
              <label className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Options (one per line)</label>
              <textarea
                data-testid="tp-custom-field-options"
                className={INPUT_CLS + " mt-1"}
                rows={4}
                value={form.options}
                onChange={(e) => setF("options", e.target.value)}
                placeholder={"Under 6ft\nOver 6ft"}
                required
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="tp-custom-field-required"
              checked={form.required}
              onChange={(e) => setF("required", e.target.checked)}
              className="accent-[#0072bc]"
            />
            Required (surface a warning if left blank on a profile)
          </label>
          {error && (
            <div className="bg-[#fff5f5] border border-[#f5c2c2] rounded-lg p-3 text-[12.5px] text-[#7a1c1c]" data-testid="tp-custom-field-error">
              {error}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e7e9ee] bg-[#f9fafb] rounded-b-xl">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-white">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !form.label.trim() || !form.key.trim()}
            data-testid="tp-custom-field-save"
            className="px-4 py-2 text-[12.5px] font-semibold rounded-lg bg-[#0072bc] text-white hover:bg-[#005a95] disabled:opacity-50"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create field"}
          </button>
        </div>
      </form>
    </div>
  );
}
